import React from "react";
import { motion } from "framer-motion";
import { 
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Globe,
  Shield,
  Home,
  Users,
  Megaphone,
  HandHeart,
  Briefcase,
  TreePine,
  Vote,
  Building,
  Linkedin,
  Instagram,
  Twitter
} from "lucide-react";

import logo from "../assets/ce/logo_w.png";

const InitiativeFooter = React.memo(() => {
  const currentYear = new Date().getFullYear();

  const initiatives = [
    { name: "Youth in Politics Initiative", icon: Vote, to: "/initiatives/youth-in-politics" },
    { name: "Youth in Cooperatives Initiative", icon: Building, to: "/initiatives/youth-in-cooperatives" }, 
    { name: "Youth Entrepreneurship Initiative", icon: Briefcase, to: "/initiatives/youth-entrepreneurship" },
    { name: "Back 2 Community", icon: HandHeart, to: "/initiatives/b2c" },
    { name: "Back 2 Roots", icon: TreePine, to: "/initiatives/b2r" }
  ];

  const quickLinks = [
    { name: "Home", icon: Home, to: "/" },
    { name: "About", icon: Users, to: "/about" },
    { name: "Contact Us", icon: Phone, to: "/contact" }
  ];

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          <defs>
            <pattern id="footer-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="2" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#footer-pattern)" className="text-white" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          
          <div className="lg:col-span-1 space-y-4">
            <motion.div 
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <img 
                src={logo} 
                alt="Initiative Organization Logo" 
                className="h-16 w-auto object-contain"
                onError={(e) => {
                  e.target.outerHTML = `
                    <div class="flex items-center space-x-3">
                      <div class="w-16 h-16 bg-gradient-to-br from-red-700 to-red-800 rounded-2xl flex items-center justify-center shadow-xl">
                        <span class="text-white font-bold text-2xl">CE</span>
                      </div>
                      <div>
                        <h3 class="text-2xl font-black text-white">INITIATIVE</h3>
                        <p class="text-red-300 text-sm font-bold tracking-[0.3em]">EXCELLENCE</p>
                        <p class="text-gray-300 text-xs">ORGANIZATION</p>
                      </div>
                    </div>
                  `;
                }}
              />
            </motion.div>
            
            <p className="text-gray-300 leading-relaxed text-sm">
              Empowering youth and strengthening communities through innovative initiatives and meaningful engagement.
            </p>

            <div className="space-y-3">
              <div>
                <p className="text-white font-semibold text-sm">
                  "ENGAGE • EMPOWER • EXCEL"
                </p>
              </div>

              <div>
                <p className="text-white font-semibold text-sm flex items-center">
                  <Shield className="w-4 h-4 mr-2" style={{ color: '#87212e' }} />
                  Certified Organization
                </p>
                <p className="text-white text-xs">Youth Development | Community Impact | Social Change</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Our Initiatives</h4>
            <ul className="space-y-2">
              {initiatives.map((initiative, index) => (
                <li key={index}>
                  <motion.a 
                    href={initiative.to} 
                    className="text-gray-300 hover:text-red-300 transition-colors duration-300 text-xs flex items-center"
                    whileHover={{ x: 3 }}
                  >
                    <initiative.icon className="w-3 h-3 mr-2" style={{ color: '#87212e' }} />
                    {initiative.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Quick Links</h4>
            <div className="grid grid-cols-1 gap-1">
              {quickLinks.map((link, index) => (
                <motion.a 
                  key={index}
                  href={link.to} 
                  className="text-gray-300 hover:text-white transition-colors duration-300 text-xs flex items-center py-1"
                  whileHover={{ x: 3 }}
                >
                  <link.icon className="w-3 h-3 mr-2" style={{ color: '#87212e' }} />
                  {link.name}
                </motion.a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            
            <div className="space-y-3">
              <h4 className="text-lg font-bold text-white">Contact Information</h4>
              <div className="space-y-2 text-xs">
                <p className="text-gray-300 flex items-center">
                  <Mail className="w-3 h-3 mr-2" style={{ color: '#87212e' }} />
                  harshsanghani2004@gmail.com
                </p>
                <p className="text-gray-300 flex items-center">
                  <Globe className="w-3 h-3 mr-2" style={{ color: '#87212e' }} />
                  www.harshsanghani.com
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="text-lg font-bold text-white">Follow Me</h4>
              <div className="space-y-3">
                <p className="text-gray-300 text-xs">
                  Stay updated with my latest initiatives and community programs.
                </p>
                <div className="flex items-center gap-3">
                  <motion.a
                    href="https://www.linkedin.com/in/harsh-sanghani"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 hover:bg-[#0077B5] rounded-lg flex items-center justify-center transition-colors duration-300"
                    whileHover={{ y: -2, scale: 1.05 }}
                  >
                    <Linkedin className="w-4 h-4" />
                  </motion.a>
                  <motion.a
                    href="https://www.instagram.com/harsh_mukeshbhai_sanghani"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 rounded-lg flex items-center justify-center transition-colors duration-300"
                    whileHover={{ y: -2, scale: 1.05 }}
                  >
                    <Instagram className="w-4 h-4" />
                  </motion.a>
                  <motion.a
                    href="https://x.com/HarshSanghani99"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 hover:bg-black rounded-lg flex items-center justify-center transition-colors duration-300"
                    whileHover={{ y: -2, scale: 1.05 }}
                  >
                    <Twitter className="w-4 h-4" />
                  </motion.a>
                </div>
                <motion.a 
                  href="/contact" 
                  className="inline-flex items-center text-red-300 hover:text-white transition-colors duration-300 text-sm"
                  whileHover={{ x: 3 }}
                >
                  Get in Touch
                  <ArrowRight className="w-3 h-3 ml-1" />
                </motion.a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-red-700/50 mt-8 pt-6">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-3 lg:space-y-0">
            <div className="text-gray-300 text-xs text-center lg:text-left">
              <p>© {currentYear} Your Organization Name. All rights reserved.</p>
              <p className="text-gray-400 mt-1">Empowering Communities • Building Future Leaders</p>
            </div>
            
            <div className="flex items-center space-x-6 text-xs text-gray-300">
              <motion.a 
                href="#" 
                className="hover:text-red-300 transition-colors duration-300"
                whileHover={{ y: -1 }}
              >
                Privacy Policy
              </motion.a>
              <motion.a 
                href="#" 
                className="hover:text-red-300 transition-colors duration-300"
                whileHover={{ y: -1 }}
              >
                Terms of Service
              </motion.a>
              <motion.a 
                href="#" 
                className="hover:text-red-300 transition-colors duration-300"
                whileHover={{ y: -1 }}
              >
                Join Us
              </motion.a>
            </div>
          </div>
          
          <div className="text-center mt-4">
            <p className="text-white font-semibold text-sm">
              BUILDING STRONGER COMMUNITIES TOGETHER
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});

export default InitiativeFooter;